import { EntityOrigin, Species } from '~graphql/generated/graphql'
import {
    AmphibianIcon,
    BirdIcon,
    CatIcon,
    CowIcon,
    DeerIcon,
    DogIcon,
    DonkeyIcon,
    FishIcon,
    GoatIcon,
    HorseIcon,
    LionIcon,
    LizardIcon,
    MarsupialIcon,
    PawIcon,
    PigIcon,
    PocketPetIcon,
    PrimateIcon,
    RabbitIcon,
    ReptileIcon,
    RodentIcon,
    SheepIcon,
    SnakeIcon,
    TurtleIcon,
    WolfIcon,
} from '~components/shared/animal-icons'
import { WidgetHeadingFont } from '~graphql/generated/graphql'
import { Dispatch, SetStateAction } from 'react'

export type SetStateFn<T> = Dispatch<SetStateAction<T>>

export enum LocalStorageKeys {
    isSidebarPinned = 'isSidebarPinned',
    /** Routes the user pinned to the Pinned submenu, in display order. */
    pinnedSidebarPages = 'pinnedSidebarPages',
    /** Whether the Pinned submenu is expanded. */
    pinnedSectionOpen = 'pinnedSectionOpen',
    /** When true, render the legacy sidebar instead of the reorganized one. */
    useLegacySidebar = 'useLegacySidebar',
    hideInactiveNotifications = 'hideInactiveNotifications',
    pinnedChats = 'pinnedChats',
    chatsFilters = 'chatsFilters',
    chatsOrderBy = 'chatsOrderBy',
    /** Legacy boolean toggle; only read to migrate into `chatsPreviewMessageSource`. */
    chatsShowClientMsgsOnly = 'chatsShowClientMsgsOnly',
    chatsPreviewMessageSource = 'chatsPreviewMessageSource',
    chatsShowMessagesForAllLocations = 'chatsShowMessagesForAllLocations',
    chatsShowErrorMsgs = 'chatsShowErrorMsgs',
    widgetServiceOrderPanelOpen = 'widgetServiceOrderPanelOpen',
    clinicServiceOrderPanelOpen = 'clinicServiceOrderPanelOpen',
    authToken = 'authToken',
    selectedClinic = 'selectedClinic',
    selectedOrganization = 'selectedOrganization',
    selectedClinicGroup = 'selectedClinicGroup',
    hasSeenDownloadPage = 'hasSeenDownloadPage',
    dailyHuddle = 'dailyHuddle',
    hasSeenTutorialPage = 'hasSeenTutorialPage',
    showInactivePhones = 'showInactivePhones',
    onlyActiveNotificationTemplates = 'onlyActiveNotificationTemplates',
    isDemoMode = 'isDemoMode',
    DEFAULT_TEST_EMAIL_RECIPIENTS = 'defaultTestEmailRecipients',
    actionItemType = 'actionItemType',
    filterActivePimsReminderTypes = 'filterActivePimsReminderTypes',
    DEFAULT_DAILY_HUDDLE_STEP = 'default_daily_huddle_step',
    showEntered = 'showEntered',
    includeSubgroups = 'includeSubgroups',
    formTextFilters = 'formTextFilters',
    closeChatOnSend = 'closeChatOnSend',
    openChatOnSend = 'openChatOnSend',
    allowCtrlEnterToSend = 'allowCtrlEnterToSend',
    massCommunicationShowArchived = 'massCommunicationShowArchived',
    massCommunicationRecipients = 'massCommunicationRecipients',
    pendingActionsSortColumn = 'pendingActionsSortColumn',
    usePerDay = 'usePerDay',
    reviewsSortBy = 'reviewsSortBy',
    teamChatsShowOtherClinics = 'teamChatsShowOtherClinics',
    teamChatsAccordionStates = 'teamChatsAccordionStates',
    clientSegmentsNewModalDontShowAgain = 'clientSegmentsNewModalDontShowAgain',
    appointmentsSelectedClinicians = 'appointmentsSelectedClinicians',
    appointmentsSelectedRooms = 'appointmentsSelectedRooms',
    clinicSelectSortBy = 'clinicSelectSortBy',
    hideAutomaticReschedulingAlert = 'hideAutomaticReschedulingAlert',
    showNonSystemClients = 'showNonSystemClients',
    whiteboardHiddenStatusIds = 'whiteboardHiddenStatusIds',
    whiteboardHideEmpty = 'whiteboardHideEmpty',
    whiteboardColumnOrder = 'whiteboardColumnOrder',
    whiteboardDateRange = 'whiteboardDateRange',
    whiteboardShowStatusBgColor = 'whiteboardShowStatusBgColor',
    whiteboardCollapsedStatusIds = 'whiteboardCollapsedStatusIds',
    whiteboardChatFilters = 'whiteboardChatFilters',
    /** Legacy boolean toggle; only read to migrate into `whiteboardPreviewMessageSource`. */
    whiteboardShowClientMsgsOnly = 'whiteboardShowClientMsgsOnly',
    whiteboardPreviewMessageSource = 'whiteboardPreviewMessageSource',
    openChatPopoverIds = 'openChatPopoverIds',
    minimizedChatPopoverIds = 'minimizedChatPopoverIds',
    chatPopoverSize = 'chatPopoverSize',
    openTeamChatPopoverIds = 'openTeamChatPopoverIds',
    minimizedTeamChatPopoverIds = 'minimizedTeamChatPopoverIds',
    teamChatPopoverSize = 'teamChatPopoverSize',
    enterpriseAppointmentsExportFields = 'enterpriseAppointmentsExportFields',
    exportMessagesAllLocationsWarningDontShowAgain = 'exportMessagesAllLocationsWarningDontShowAgain',
    inactivityState = 'inactivityState',
    petSelfieSlideshowTheme = 'petSelfieSlideshowTheme',
    petSelfieSlideshowExcludedCaptionIds = 'petSelfieSlideshowExcludedCaptionIds',
    whatsNewSeenIds = 'whatsNewSeenIds',
}

export const getEmptyAvailabilityState = () => {
    const EmptyAvailabilityState = Object.freeze({
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
    })

    return { ...EmptyAvailabilityState }
}

String.prototype.capitalize = function () {
    return this.charAt(0).toUpperCase() + this.slice(1)
}

export const FALLBACK_AVATAR =
    'https://firebasestorage.googleapis.com/v0/b/chckup-server-dev.appspot.com/o/default%20avatar.jpeg?alt=media&token=95c9943d-9cd8-445a-93b6-73106d23c079'

export const speciesIconMap: {
    [key: string]: React.FC<{ className?: string }>
} = {
    [Species.Amphibian]: AmphibianIcon,
    [Species.Bird]: BirdIcon,
    [Species.Cat]: CatIcon,
    [Species.Cow]: CowIcon,
    [Species.Deer]: DeerIcon,
    [Species.Dog]: DogIcon,
    [Species.Donkey]: DonkeyIcon,
    [Species.Ferret]: RodentIcon,
    [Species.Fish]: FishIcon,
    [Species.Goat]: GoatIcon,
    [Species.Horse]: HorseIcon,
    [Species.Lion]: LionIcon,
    [Species.Lizard]: LizardIcon,
    [Species.Marsupial]: MarsupialIcon,
    [Species.Panther]: CatIcon,
    [Species.Pig]: PigIcon,
    [Species.PocketPet]: PocketPetIcon,
    [Species.Primate]: PrimateIcon,
    [Species.Rabbit]: RabbitIcon,
    [Species.Reptile]: ReptileIcon,
    [Species.Rodent]: RodentIcon,
    [Species.Sheep]: SheepIcon,
    [Species.Snake]: SnakeIcon,
    [Species.Turtle]: TurtleIcon,
    [Species.Wolf]: WolfIcon,
    [Species.Other]: PawIcon,
}

export enum SupabaseEntities {
    ActionItem = 'ActionItem',
    Appointment = 'Appointment',
    Client = 'Client',
    Clinic = 'Clinic',
    Clinician = 'Clinician',
    Room = 'Room',
    ConnectInstallation = 'ConnectInstallation',
    Patient = 'Patient',
    User = 'User',
    Organization = 'Organization',
    PmsApptType = 'PmsApptType',
    ClinicGroup = 'ClinicGroup',
    Payment = 'Payment',
    PimsReminderType = 'PimsReminderType',
    Product = 'Product',
    ReportMetric = 'ReportMetric',
    Form = 'Form',
    ClientForm = 'ClientForm',
    Call = 'Call',
    SchedulingRules = 'SchedulingRules',
}

export const getSupabaseLink = (entity: SupabaseEntities, id: string) => {
    return `https://app.supabase.com/project/${
        process.env.SUPABASE_PROJECT_ID
    }/editor/${getTableId(entity)}/?filter=id%3Aeq%3A${id}`
}

// map each environment to dictionary of entity types to table ids
const getTableId = (entity: SupabaseEntities) => {
    if (process.env.NODE_ENV === 'production') {
        return ProdTableIds[entity]
    } else if (process.env.NODE_ENV === 'staging') {
        return StagingTableIds[entity]
    } else {
        return DevTableIds[entity]
    }
}

export type PageSizeType = 5 | 10 | 25 | 50 | 100

export const PAGE_SIZES: PageSizeType[] = [5, 10, 25, 50, 100]

const DevTableIds: Partial<{ [key in SupabaseEntities]: string }> = {
    ActionItem: '2034794',
    Appointment: '2034811',
    Client: '2034819',
    Clinic: '2034971',
    Clinician: '2034963',
    Room: '2035067',
    ConnectInstallation: '2034980',
    Patient: '2034828',
    User: '2035092',
    Organization: '2035025',
    Payment: '2035033',
    PmsApptType: '',
    Product: '',
    ClinicGroup: '',
    PimsReminderType: '',
    ReportMetric: '',
    Form: '',
    ClientForm: '',
    SchedulingRules: '',
}

const StagingTableIds: Partial<{ [key in SupabaseEntities]: string }> = {
    ActionItem: 'staging_action_items',
    Appointment: 'staging_appointments',
    Client: 'staging_clients',
    Clinic: 'staging_clinics',
    Clinician: 'staging_clinicians',
    Room: '',
    ConnectInstallation: 'staging_connect_installations',
    Patient: 'staging_patients',
    User: '',
    Organization: 'staging_organizations',
    Payment: '',
    PmsApptType: '',
    Product: '',
    ClinicGroup: '',
    PimsReminderType: '',
    ReportMetric: '',
    Form: '',
    ClientForm: '',
    SchedulingRules: '',
}

const ProdTableIds: Partial<{ [key in SupabaseEntities]: string }> = {
    ActionItem: '32343',
    Appointment: '29032',
    Client: '29049',
    Clinic: '29141',
    Clinician: '29133',
    Room: '29205',
    ConnectInstallation: '29149',
    Patient: '29173',
    User: '29229',
    Organization: '29165',
    Payment: '29181',
    PmsApptType: '',
    Product: '',
    ClinicGroup: '',
    PimsReminderType: '',
    ReportMetric: '',
    Form: '',
    ClientForm: '',
    SchedulingRules: '',
}

// Array.prototype.map = function <T extends Record<string, unknown>>(item: T) {
//     return this.map(i => i[item])
// }

export const CHCKUP_ORIGINS: EntityOrigin[] = [
    EntityOrigin.ChckupWidget,
    EntityOrigin.ChckupWebsite,
    EntityOrigin.ChckupDashboard,
]

export const DEFAULT_TIME_ZONE = 'America/New_York'

export const WidgetHeadingFonts: Record<WidgetHeadingFont, string> = {
    [WidgetHeadingFont.Simple]: '"Inter", sans-serif',
    [WidgetHeadingFont.Bold]: '"Poppins", sans-serif',
    [WidgetHeadingFont.Refined]: '"DM Serif Display", sans-serif',
    [WidgetHeadingFont.Fun]: '"Lilita One", sans-serif',
}

export const HIGHLIGHT_STYLE = {
    bg: 'brand.100',
}
